// BaseLRUStore.ts
import { LRUCache } from 'lru-cache';

export type BaseLRUStoreConfig<K, V> = {
  /** Maximum number of entries. Recommended as a safety cap when using maxTotalSize. */
  maxEntries?: number;
  /** Maximum total size in bytes (requires sizeCalculation). */
  maxTotalSize?: number;
  sizeCalculation?: (value: V, key: K) => number;
};

export abstract class BaseLRUStore<K = string, V = unknown> {
  protected cache: LRUCache<K, V>;
  private cacheConfig: BaseLRUStoreConfig<K, V>;

  constructor(config: BaseLRUStoreConfig<K, V> | number = 100) {
    this.cacheConfig =
      typeof config === 'number' ? { maxEntries: config } : { ...config };

    if (!this.cacheConfig.maxEntries && !this.cacheConfig.maxTotalSize) {
      this.cacheConfig.maxEntries = 100;
    }

    this.cache = this.createCache();
  }

  private createCache(): LRUCache<K, V> {
    return new LRUCache<K, V>({
      max: this.cacheConfig.maxEntries,
      maxSize: this.cacheConfig.maxTotalSize,
      sizeCalculation: this.cacheConfig.sizeCalculation,
      dispose: (value, key) =>
        queueMicrotask(() => {
          this.onEvict(key, value);
        }),
    });
  }

  get(key: K): V | undefined {
    return this.cache.get(key);
  }

  set(key: K, value: V): void {
    this.cache.set(key, value);
  }

  delete(key: K): void {
    this.cache.delete(key);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
  /**
   * workAround to protect keys from eviction.
   * @param key
   */
  touchBulk(key: K[]): void {
    key.forEach((k) => this.cache.get(k));
  }

  /**
   * workAround to protect keys from eviction.
   * @param key
   */
  touch(key: K): void {
    this.cache.get(key);
  }

  maxAllowedEntries(): number {
    return this.cache.max ?? 0;
  }

  maxAllowedTotalSize(): number {
    return this.cache.maxSize ?? 0;
  }

  maxAllowedSize(): number {
    return this.cache.max ?? 0;
  }

  currentTotalSize(): number {
    return this.cache.calculatedSize;
  }

  setMaxTotalSize(bytes: number): void {
    if (bytes <= 0 || this.cache.maxSize === bytes) {
      return;
    }

    const entries = [...this.cache.entries()];
    this.cacheConfig.maxTotalSize = bytes;
    this.cache = this.createCache();

    for (const [key, value] of entries) {
      this.cache.set(key, value);
    }
  }

  protected onEvict(key: K, value: V): void {
    // extended classes can implement this method to handle eviction logic
  }
}
